import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'

import { useParams, useNavigate, useSearchParams } from 'react-router-dom'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useContentCategories } from '@/core/hooks/misc/useContentCategories'
import { useMoods } from '@/core/hooks/misc/useMoods'
import { useMusicGenres } from '@/core/hooks/misc/useMusicGenres'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useAssignedPlaylists,
	useInUsePlaylists,
	useFavoritePlaylists,
	usePlaylists,
	useAddPlaylistToFavorites,
} from '@/core/hooks/usePlaylist'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { useProfileCompletion } from './hooks/useProfileCompletion'
import ViewAllPlaylistsPageUI from './ViewAllPlaylistsPageUI'

type PlaylistType = 'assigned' | 'favorites' | 'all' | 'in-use'

const ViewAllPlaylistsPage: React.FC = () => {
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const { type } = useParams<{ type: PlaylistType }>()
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const [searchQuery, setSearchQuery] = useState('')
	const debouncedSearchQuery = useDebounce(searchQuery, 500)
	const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
	const [currentPage, setCurrentPage] = useState(1)
	const [allPlaylists, setAllPlaylists] = useState<Playlist[]>([])
	const processedPagesRef = useRef<Set<number>>(new Set())
	const [isLoadingMorePages, setIsLoadingMorePages] = useState(false)

	// Get filter values from URL params
	const moodFilter = searchParams.get('moods')
	const contentCategoryFilter = searchParams.get('content_category_ids')
	const musicGenreFilter = searchParams.get('music_genre_ids')

	// Fetch filters to get display names
	const { data: moods = [] } = useMoods()
	const { data: contentCategories = [] } = useContentCategories()
	const { data: musicGenres = [] } = useMusicGenres()

	// Get section name based on filter
	const sectionName = useMemo(() => {
		if (moodFilter) {
			const mood = moods.find((m) => m === moodFilter)
			return mood ? mood.charAt(0).toUpperCase() + mood.slice(1) : null
		}
		if (contentCategoryFilter) {
			const category = contentCategories.find(
				(c) => c.id === Number(contentCategoryFilter)
			)
			return category ? category.getLocalizedName('en-US') : null
		}
		if (musicGenreFilter) {
			const genre = musicGenres.find((g) => g.id === Number(musicGenreFilter))
			return genre ? genre.name : null
		}
		return null
	}, [
		moodFilter,
		contentCategoryFilter,
		musicGenreFilter,
		moods,
		contentCategories,
		musicGenres,
	])

	// Scroll to top when entering the page or filter changes
	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}, [type, moodFilter, contentCategoryFilter, musicGenreFilter])

	// Validate type and redirect if invalid
	useEffect(() => {
		if (!type || !['assigned', 'favorites', 'all', 'in-use'].includes(type)) {
			navigate('/music', { replace: true })
		}
	}, [type, navigate])

	// Check profile completion
	const { data: profileCompletion, refetch: refetchProfileCompletion } =
		useProfileCompletion()

	// Refetch profile completion when component mounts to ensure fresh data
	useEffect(() => {
		refetchProfileCompletion()
	}, [refetchProfileCompletion])

	// Fetch playlists based on type
	const assignedQuery = useAssignedPlaylists({
		config: useAccountConfig({
			params: {
				public: false,
				per_page: 10,
				page: currentPage,
			},
		}),
		options: {
			enabled: type === 'assigned',
		},
	})

	const favoritesQuery = useFavoritePlaylists({
		config: useAccountConfig({
			params: {
				per_page: 10,
				page: currentPage,
			},
		}),
		options: {
			enabled: type === 'favorites',
		},
	})

	const inUseQuery = useInUsePlaylists({
		config: useAccountConfig({
			params: {
				per_page: 10,
				page: currentPage,
			},
		}),
		options: {
			enabled: type === 'in-use',
		},
	})

	// Always fetch favorite playlists to check favorite status (for all types)
	const { data: allFavoritePlaylistsData, refetch: refetchAllFavorites } =
		useFavoritePlaylists({
			config: useAccountConfig({
				params: {
					per_page: 100, // Fetch enough to cover most cases
				},
			}),
			options: {
				enabled: true, // Always enabled to check favorite status
			},
		})

	// Build query params for all playlists (including filters)
	const allPlaylistsParams = useMemo(() => {
		const params: Record<string, any> = {
			per_page: 10,
			page: currentPage,
		}

		// Add filter params if present
		if (moodFilter) {
			params.moods = moodFilter
			params.public = true
		}
		if (contentCategoryFilter) {
			params.content_category_ids = contentCategoryFilter
			params.public = true
		}
		if (musicGenreFilter) {
			params.music_genre_ids = musicGenreFilter
			params.public = true
		}

		return params
	}, [currentPage, moodFilter, contentCategoryFilter, musicGenreFilter])

	const allPlaylistsQuery = usePlaylists({
		config: useAccountConfig({
			params: allPlaylistsParams,
		}),
		options: {
			enabled: type === 'all',
		},
	})

	// Search playlists
	const { data: searchPlaylistsData, isLoading: isLoadingSearch } =
		usePlaylists({
			config: useAccountConfig({
				params: {
					query: debouncedSearchQuery,
					include: 'music_genres',
					per_page: 25,
				},
			}),
			options: {
				enabled: debouncedSearchQuery.length > 0,
			},
		})

	// Get the active query based on type
	const activeQuery =
		type === 'assigned'
			? assignedQuery
			: type === 'favorites'
				? favoritesQuery
				: type === 'in-use'
					? inUseQuery
					: allPlaylistsQuery

	const isLoading = activeQuery.isLoading
	const pagination = activeQuery.data?.meta?.pagination

	// Determine which playlists to show
	const showSearchResults = debouncedSearchQuery.length > 0

	// Accumulate playlists from all loaded pages
	useEffect(() => {
		if (showSearchResults) {
			// Reset accumulated playlists when searching
			setAllPlaylists([])
			processedPagesRef.current = new Set()
			setCurrentPage(1)
			setIsLoadingMorePages(false)
			return
		}

		const newPlaylists = activeQuery.data?.data || []
		if (
			newPlaylists.length > 0 &&
			!processedPagesRef.current.has(currentPage)
		) {
			// Process this page's data only if we haven't processed it yet
			if (currentPage === 1) {
				// First page - replace all
				setAllPlaylists(newPlaylists)
			} else {
				// Subsequent pages - append
				setAllPlaylists((prevPlaylists) => {
					// Avoid duplicates by checking IDs
					const existingIds = new Set(prevPlaylists.map((p) => p.id))
					const uniqueNewPlaylists = newPlaylists.filter(
						(p) => !existingIds.has(p.id)
					)
					return [...prevPlaylists, ...uniqueNewPlaylists]
				})
			}
			// Mark this page as processed
			processedPagesRef.current.add(currentPage)
			setIsLoadingMorePages(false)
		}
	}, [activeQuery.data, currentPage, showSearchResults])

	const playlists = showSearchResults
		? searchPlaylistsData?.data || []
		: allPlaylists
	// isLoadingMore should be true when we're loading more pages (not initial load)
	const isLoadingMore = isLoadingMorePages

	// Create a Set of favorite playlist IDs for quick lookup
	const favoritePlaylistIds = React.useMemo(() => {
		const favoritePlaylists = allFavoritePlaylistsData?.data || []
		return new Set(favoritePlaylists.map((playlist) => playlist.id))
	}, [allFavoritePlaylistsData])

	// Track which playlist is currently being favorited/unfavorited
	const [loadingPlaylistId, setLoadingPlaylistId] = useState<number | null>(
		null
	)
	const [isFavoriting, setIsFavoriting] = useState<boolean>(true)

	// Mutation for adding playlist to favorites
	const addToFavoritesMutation = useAddPlaylistToFavorites({
		config: useAccountConfig({}),
		options: {
			onSuccess: (
				_: void,
				variables: { playlistId: number; favorite: boolean }
			) => {
				setLoadingPlaylistId(null)
				activeQuery.refetch()
				refetchAllFavorites() // Refetch favorites to update the Set
				showNotification({
					message: variables.favorite
						? t('entities.music.playlist_added_to_favorites')
						: t('entities.music.playlist_removed_from_favorites'),
					type: 'success',
				})
			},
			onError: () => {
				setLoadingPlaylistId(null)
				showNotification({
					message: isFavoriting
						? t('entities.music.playlist_add_to_favorites_error')
						: t('entities.music.playlist_remove_from_favorites_error'),
					type: 'error',
				})
			},
		},
	})

	// Handle favorite click
	const handleFavoriteClick = useCallback(
		(playlist: Playlist, isFavoritesCarousel: boolean) => {
			setLoadingPlaylistId(playlist.id)

			// Determine the favorite state based on:
			// 1. If in Favorites carousel → always unfavorite (favorite: false)
			// 2. If in other carousels → toggle based on current favorite status
			let favorite: boolean
			if (isFavoritesCarousel) {
				// In favorites carousel, always unfavorite
				favorite = false
			} else {
				// In other carousels, check if playlist is currently favorited
				const isCurrentlyFavorite = favoritePlaylistIds.has(playlist.id)
				// If already favorited, unfavorite it; otherwise, favorite it
				favorite = !isCurrentlyFavorite
			}

			setIsFavoriting(favorite)
			addToFavoritesMutation.mutate({ playlistId: playlist.id, favorite })
		},
		[addToFavoritesMutation, favoritePlaylistIds]
	)

	// Handle load more (infinite scroll)
	const handleLoadMore = useCallback(() => {
		if (!pagination || currentPage >= pagination.total_pages || isLoading) {
			return
		}
		const nextPage = currentPage + 1
		setIsLoadingMorePages(true)
		setCurrentPage(nextPage)
	}, [currentPage, pagination, isLoading])

	// Get page title based on type and filters
	const getPageTitle = () => {
		if (!type) return t('navigation.music')
		switch (type) {
			case 'assigned':
				return t('entities.music.custom_playlists')
			case 'in-use':
				return t('entities.music.assigned_playlists')
			case 'favorites':
				return t('entities.music.favorites')
			case 'all':
				// If there's a filter, show the section name instead of "All playlists"
				return sectionName || t('entities.music.all_playlists')
			default:
				return t('navigation.music')
		}
	}

	// Don't render if type is invalid
	if (!type || !['assigned', 'favorites', 'all', 'in-use'].includes(type)) {
		return null
	}

	return (
		<>
			<ViewAllPlaylistsPageUI
				title={getPageTitle()}
				searchQuery={searchQuery}
				onSearchChange={setSearchQuery}
				playlists={playlists}
				isLoading={showSearchResults ? isLoadingSearch : isLoading}
				showSearchResults={showSearchResults}
				onFavoriteClick={handleFavoriteClick}
				loadingPlaylistId={loadingPlaylistId}
				onRequestCustomPlaylistClick={() => setIsRequestModalOpen(true)}
				isRequestModalOpen={isRequestModalOpen}
				onCloseRequestModal={() => setIsRequestModalOpen(false)}
				profileIncomplete={!profileCompletion?.isComplete}
				hasMore={pagination ? currentPage < pagination.total_pages : false}
				pagination={pagination}
				currentPage={currentPage}
				isLoadingMore={isLoadingMore}
				onLoadMore={handleLoadMore}
				playlistType={type}
				favoritePlaylistIds={favoritePlaylistIds}
			/>
		</>
	)
}

export default ViewAllPlaylistsPage
